import { PANEL_TYPE_ALIASES, PURMO_COMPACT, type RadiatorCatalogueEntry } from "@sts/rules-data";
import { correctEmitterOutput } from "./emitters.js";

/**
 * The largest radiator that fits a space, and the smallest that keeps the
 * room warm — for each place a radiator can go in a room.
 *
 * The same range and the same arithmetic the plan's design check uses: Purmo
 * Compact sizes (the plan's radiators), each row's own ΔT50 output and
 * exponent from the catalogue, corrected to the design flow and return
 * temperatures at the room's design temperature (`correctEmitterOutput`). A
 * row with no exponent is skipped: no default is assumed.
 *
 * "Fits" is the radiator's own width and height inside the space; the valves
 * and pipe tails want a little room either side, and the page says so. With
 * more than one space, the room's heat loss is shared between them in
 * proportion to what each could give at most.
 */

export interface SpaceFit {
  readonly widthMm: number;
  readonly heightMm: number;
  readonly label?: string;
  /** The most any radiator in the range that fits gives here, and which. */
  readonly largest: FitChoice | null;
  /** The smallest that gives this space's share of the heat loss; null when none does. */
  readonly smallestThatCovers: FitChoice | null;
  /** This space's share of the room's heat loss, W. */
  readonly shareW: number;
}

export interface FitChoice {
  readonly catalogueId: string;
  /** "Type 22 (K2) 600 × 1200 mm" */
  readonly label: string;
  readonly heightMm: number;
  readonly lengthMm: number;
  readonly outputW: number;
}

export interface RadiatorFit {
  readonly spaces: readonly SpaceFit[];
  /** Every space at its largest, W. */
  readonly maxOutputW: number;
  readonly loadW: number;
  /** The spaces can hold enough radiator for the room at this flow temperature. */
  readonly enough: boolean;
}

export interface RadiatorFitInput {
  readonly spaces: readonly { readonly widthMm: number; readonly heightMm: number; readonly label?: string }[];
  /** The room's design heat loss, W. */
  readonly loadW: number;
  readonly roomTempC: number;
  readonly flowC: number;
  readonly returnC: number;
}

function outputOf(e: RadiatorCatalogueEntry, input: RadiatorFitInput): number | null {
  if (e.exponent === undefined) return null;
  return correctEmitterOutput({
    ratedOutputW: e.ratedOutputW,
    flowTempC: input.flowC,
    returnTempC: input.returnC,
    roomDesignTempC: input.roomTempC,
    exponent: e.exponent,
    ratedDeltaTK: e.ratedDeltaTK,
  }).outputW;
}

function choice(e: RadiatorCatalogueEntry, outputW: number): FitChoice {
  const type = e.panelType ? `${PANEL_TYPE_ALIASES[e.panelType]} (${e.panelType}) ` : "";
  return { catalogueId: e.id, label: `${type}${e.heightMm} × ${e.lengthMm} mm`, heightMm: e.heightMm, lengthMm: e.lengthMm, outputW: Math.round(outputW) };
}

export function radiatorFit(input: RadiatorFitInput): RadiatorFit {
  const rated = PURMO_COMPACT.data.entries
    .filter((e) => e.panelType !== undefined)
    .map((e) => ({ e, w: outputOf(e, input) }))
    .filter((x): x is { e: RadiatorCatalogueEntry; w: number } => x.w !== null && x.w > 0);

  const fitting = input.spaces.map((s) => rated.filter((x) => x.e.heightMm <= s.heightMm && x.e.lengthMm <= s.widthMm));
  const largest = fitting.map((list) => list.reduce<{ e: RadiatorCatalogueEntry; w: number } | null>((best, x) => (!best || x.w > best.w ? x : best), null));
  const maxOutputW = largest.reduce((s, l) => s + (l?.w ?? 0), 0);
  const load = Math.max(0, input.loadW);

  const spaces: SpaceFit[] = input.spaces.map((s, i) => {
    const big = largest[i] ?? null;
    const shareW = maxOutputW > 0 && big ? (load * big.w) / maxOutputW : load / Math.max(1, input.spaces.length);
    // The least radiator that still does its share: the lowest output at or
    // above it, and of those the smallest face.
    const covering = fitting[i]!
      .filter((x) => x.w >= shareW)
      .sort((a, b) => a.w - b.w || a.e.heightMm * a.e.lengthMm - b.e.heightMm * b.e.lengthMm)[0];
    return {
      widthMm: s.widthMm,
      heightMm: s.heightMm,
      ...(s.label ? { label: s.label } : {}),
      largest: big ? choice(big.e, big.w) : null,
      smallestThatCovers: covering ? choice(covering.e, covering.w) : null,
      shareW: Math.round(shareW),
    };
  });
  return { spaces, maxOutputW: Math.round(maxOutputW), loadW: Math.round(load), enough: maxOutputW >= load && load > 0 };
}
